import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TextField, Button, Grid2, Typography, Tooltip, Box, LinearProgress } from '@mui/material';
import { SelectField } from 'components';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { enqueueSnackbar } from 'actions';
import { VTKDataRefreshRequest } from '../requests/DataManagementRequests';

// Moved outside component to avoid recreation on every render
const PLATFORM_OPTIONS = [
	{ value: 'invalid', disabled: true, text: 'Select one option' },
	{ value: 'Foresight', disabled: false, text: 'Foresight' },
	{ value: 'PrOptima', disabled: true, text: 'PrOptima' },
];

const FILE_NOMENCLATURE = [
	'VTK_S12_2G_Side_part1*.csv',
	'VTK_S12_2G_Side_part2*.csv',
	'VTK_S12_3G_Side_part1*.csv',
	'VTK_S12_3G_Side_part2*.csv',
	'VTK_S12_4G_Side_part1_Ericsson*.csv',
	'VTK_S12_4G_Side_part2_Ericsson*.csv',
	'VTK_S12_4G_Side_part1_Nokia*.csv',
	'VTK_S12_4G_Side_part2_Nokia*.csv',
	'',
	'VTK_2G_Side_B3_Cell_List*.csv',
	'VTK_3G_Side_B3_Cell_List*.csv',
	'VTK_4G_Side_B3_Cell_List*.csv',
];

const VTKDataRefreshForm = () => {
	const userId = useSelector(state => state.session.user.id);
	const dispatch = useDispatch();

	const [fileName, setFileName] = useState('');
	const [selectedFile, setSelectedFile] = useState(null);
	const [platform, setPlatform] = useState('Foresight');
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);

	const handlePlatformChange = (value) => {
		setPlatform(value);
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setSelectedFile(file);
			setFileName(file.name);
		}
	};

	const resetForm = () => {
		setPlatform('Foresight');
		setSelectedFile(null);
		setFileName('');
		setUploadProgress(0);
	};

	const handleSubmit = async () => {
		if (!selectedFile) {
			dispatch(enqueueSnackbar({ message: 'Please select a file.', options: { variant: 'error' } }));
			return;
		}

		const formData = new FormData();
		formData.append('File', selectedFile);
		formData.append('UserId', userId);
		formData.append('Platform', platform);

		try {
			setIsUploading(true);
			const response = await VTKDataRefreshRequest(formData, (percent) => setUploadProgress(percent));
			dispatch(enqueueSnackbar({ message: response.data.message, options: { variant: 'success' } }));
			resetForm();
		} catch (error) {
			dispatch(enqueueSnackbar({ message: `${error}`, options: { variant: 'error' } }));
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<Grid2 container spacing={2}>
			<Grid2 item size={12}>
				<Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
					Please select a pair of files to load belonging to the same technology, network side and covering the same list of consecutive days.
					<br />Beware that once the above criteria are met, the manual upload will substitute the content of the tables underlying the S12 Monitoring Dashboards.
					<br />
					<br /><strong><u>Proceed at your own risk.</u></strong>
				</Typography>
			</Grid2>

			<Grid2 item size={12}>
				<SelectField
					id="inputPlatform"
					name="inputPlatform"
					value={platform}
					onChange={(e) => handlePlatformChange(e.target.value)}
					hintText="Platform"
					menuItems={PLATFORM_OPTIONS}
				/>
			</Grid2>

			<Grid2 item size={12}>
				<TextField
					label="File Name"
					variant="outlined"
					fullWidth
					InputProps={{ readOnly: true, disabled: true }}
					value={fileName}
				/>
			</Grid2>

			<Grid2 item size={12}>
				<Tooltip
					title={
						<Box
							component="table"
							sx={{
								border: '1px solid #ccc',
								boxShadow: 1,
								p: 1,
								borderRadius: 1,
								'& td': { padding: '4px 8px' },
							}}
						>
							<tbody>
								<tr>
									<td>
										<strong>Files must contain the following nomenclature</strong>
										<br />
										<em>(Side: TFinTF, VFinTF or TFinVF)</em>
									</td>
								</tr>
								{FILE_NOMENCLATURE.map((name, idx) => (
									<tr key={idx}>
										<td>{name}</td>
									</tr>
								))}
							</tbody>
						</Box>
					}
					placement="right"
					arrow
				>
					<Button
						variant="contained"
						component="label"
						fullWidth
						startIcon={<UploadFileIcon />}
						disabled={isUploading}
					>
						Upload File
						<input type="file" accept=".zip, .csv" hidden onChange={handleFileChange} />
					</Button>
				</Tooltip>
			</Grid2>

			{isUploading && (
				<Grid2 item size={12}>
					<LinearProgress variant="determinate" value={uploadProgress} />
					<Typography variant="caption" align="center" display="block" sx={{ mt: 0.5 }}>
						{uploadProgress}%
					</Typography>
				</Grid2>
			)}

			<Grid2 item size={12}>
				<Button
					variant="contained"
					color="primary"
					fullWidth
					onClick={handleSubmit}
					disabled={isUploading}
				>
					{isUploading ? `Uploading... ${uploadProgress}%` : 'Submit'}
				</Button>
			</Grid2>
		</Grid2>
	);
};

export default VTKDataRefreshForm;